/* global initLazyImages */
/**
 * Filterleiste ueber dem Produktgrid (Markup: snippets/g24-filter-bar.liquid).
 *
 * Jede Aenderung laedt nur diese Section neu (Section Rendering API) und tauscht
 * Produktgrid und Filterleiste aus. Offenes Dropdown, Suchbegriff und Scrollposition
 * bleiben dabei erhalten, damit man mehrere Werte hintereinander anklicken kann.
 *
 * fetch bewusst ohne init-Objekt (kein AbortSignal, kein Accept): Apps im Shop wrappen
 * window.fetch und verschlucken sich daran. Veraltete Antworten werden per Zaehler verworfen.
 */
if (!customElements.get('g24-filter-bar')) {
  class G24FilterBar extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.desktop = window.matchMedia('(min-width: 768px)');
      this.requestId = 0;

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('input', this.onInput.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
      this.addEventListener('submit', (evt) => {
        evt.preventDefault();
        this.apply(this.formParams());
      });

      this.onDocClick = (evt) => {
        if (this.desktop.matches && !evt.target.closest('.g24fb__filter')) this.closeFilters();
      };
      document.addEventListener('click', this.onDocClick);

      this.onPopState = () => this.apply(new URLSearchParams(window.location.search), { push: false });
      window.addEventListener('popstate', this.onPopState);

      this.onBreakpoint = () => {
        this.closeDrawer();
        this.closeFilters();
      };
      this.desktop.addEventListener('change', this.onBreakpoint);

      this.onScroll = () => {
        if (this.scrollTicking) return;
        this.scrollTicking = true;
        requestAnimationFrame(() => {
          this.scrollTicking = false;
          this.updateStickyTop();
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });

      this.syncAllPrices();
      this.updateStickyTop();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
      window.removeEventListener('popstate', this.onPopState);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      this.desktop.removeEventListener('change', this.onBreakpoint);
      document.documentElement.classList.remove('g24fb-lock');
    }

    get form() {
      return this.querySelector('.g24fb__form');
    }

    get results() {
      return document.getElementById('filter-results');
    }

    /* ---------- Events ---------- */

    onClick(evt) {
      const toggle = evt.target.closest('.js-g24fb-toggle');
      if (toggle) {
        this.toggleFilter(toggle.closest('.g24fb__filter'));
        return;
      }

      if (evt.target.closest('.js-g24fb-open')) {
        this.openDrawer();
        return;
      }

      if (evt.target.closest('.js-g24fb-close-drawer')) {
        this.closeDrawer();
        return;
      }

      if (evt.target.closest('.js-g24fb-show')) {
        this.closeFilters();
        this.closeDrawer();
        this.scrollToResults();
        return;
      }

      const link = evt.target.closest('a.js-g24fb-link');
      if (link) {
        evt.preventDefault();
        const params = new URL(link.href, window.location.origin).searchParams;
        this.apply(params);
      }
    }

    onChange(evt) {
      const { target } = evt;

      if (target.matches('.js-g24fb-search')) return;

      if (target.matches('.js-g24fb-range')) {
        this.syncPriceFromRange(target);
        this.apply(this.formParams());
        return;
      }

      if (target.matches('.js-g24fb-price')) {
        this.syncPriceFromNumber(target);
        this.apply(this.formParams());
        return;
      }

      if (target.matches('input[type="checkbox"], select')) {
        this.apply(this.formParams());
      }
    }

    onInput(evt) {
      const { target } = evt;

      if (target.matches('.js-g24fb-search')) {
        this.filterOptions(target);
        return;
      }

      if (target.matches('.js-g24fb-range')) {
        this.syncPriceFromRange(target);
      }
    }

    onKeydown(evt) {
      if (evt.key !== 'Escape') return;

      const openFilter = this.querySelector('.g24fb__filter.is-open');
      if (this.desktop.matches && openFilter) {
        this.closeFilters();
        openFilter.querySelector('.js-g24fb-toggle').focus();
      } else if (this.classList.contains('is-drawer-open')) {
        this.closeDrawer();
      }
    }

    /* ---------- Dropdowns und Drawer ---------- */

    toggleFilter(filter) {
      if (!filter) return;
      const willOpen = !filter.classList.contains('is-open');
      this.closeFilters();
      if (willOpen) this.openFilter(filter);
    }

    openFilter(filter) {
      filter.classList.add('is-open');
      filter.querySelector('.js-g24fb-toggle').setAttribute('aria-expanded', 'true');
      this.syncAllPrices();

      // Dropdown am rechten Rand nach links aufklappen, statt aus dem Bild zu laufen.
      if (this.desktop.matches) {
        filter.classList.remove('is-flip');
        const panel = filter.querySelector('.g24fb__panel');
        if (panel && panel.getBoundingClientRect().right > window.innerWidth - 16) {
          filter.classList.add('is-flip');
        }
      }
    }

    closeFilters() {
      this.querySelectorAll('.g24fb__filter.is-open').forEach((filter) => {
        filter.classList.remove('is-open');
        filter.querySelector('.js-g24fb-toggle').setAttribute('aria-expanded', 'false');
      });
    }

    openDrawer() {
      this.classList.add('is-drawer-open');
      document.documentElement.classList.add('g24fb-lock');
      const opener = this.querySelector('.js-g24fb-open');
      if (opener) opener.setAttribute('aria-expanded', 'true');
      const close = this.querySelector('.js-g24fb-close-drawer');
      if (close) close.focus({ preventScroll: true });
    }

    closeDrawer() {
      if (!this.classList.contains('is-drawer-open')) return;
      this.classList.remove('is-drawer-open');
      document.documentElement.classList.remove('g24fb-lock');
      const opener = this.querySelector('.js-g24fb-open');
      if (opener) {
        opener.setAttribute('aria-expanded', 'false');
        if (!this.desktop.matches) opener.focus({ preventScroll: true });
      }
    }

    scrollToResults() {
      const { results } = this;
      if (!results) return;
      const offset = this.desktop.matches ? 16 : this.offsetHeight + (parseFloat(this.style.getPropertyValue('--g24fb-top')) || 0);
      const top = results.getBoundingClientRect().top - offset;
      if (top < 0) window.scrollTo({ top: window.scrollY + top, behavior: 'smooth' });
    }

    /* Mobile Kopfzeile klebt unter dem Header, solange der sichtbar ist. */
    updateStickyTop() {
      if (this.desktop.matches) return;
      let top = 0;
      const header = document.querySelector('.cc-header--sticky') || document.querySelector('#shopify-section-header');
      if (header) {
        const pos = getComputedStyle(header).position;
        if (pos === 'sticky' || pos === 'fixed') {
          top = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
        }
      }
      if (top !== this.stickyTop) {
        this.stickyTop = top;
        this.style.setProperty('--g24fb-top', `${top}px`);
      }
    }

    /* ---------- Suche im Dropdown ---------- */

    filterOptions(input) {
      const term = input.value.trim().toLowerCase();
      const filter = input.closest('.g24fb__filter');
      filter.querySelectorAll('.g24fb__opt').forEach((opt) => {
        opt.hidden = term !== '' && !opt.dataset.label.includes(term);
      });
    }

    /* ---------- Preis ---------- */

    syncAllPrices() {
      this.querySelectorAll('.g24fb__price').forEach((price) => this.paintRange(price));
    }

    syncPriceFromRange(range) {
      const price = range.closest('.g24fb__price');
      const [minRange, maxRange] = price.querySelectorAll('.js-g24fb-range');
      let lo = Number(minRange.value);
      let hi = Number(maxRange.value);

      if (lo > hi) {
        if (range === minRange) lo = hi;
        else hi = lo;
        minRange.value = lo;
        maxRange.value = hi;
      }

      price.querySelector('.js-g24fb-price[data-role="min"]').value = lo;
      price.querySelector('.js-g24fb-price[data-role="max"]').value = hi;
      this.paintRange(price);
    }

    syncPriceFromNumber(input) {
      const price = input.closest('.g24fb__price');
      const max = Number(price.dataset.max) || 0;
      const minInput = price.querySelector('.js-g24fb-price[data-role="min"]');
      const maxInput = price.querySelector('.js-g24fb-price[data-role="max"]');
      let lo = Math.max(0, Math.min(max, Math.floor(Number(minInput.value) || 0)));
      let hi = maxInput.value === '' ? max : Math.max(0, Math.min(max, Math.ceil(Number(maxInput.value) || 0)));

      if (lo > hi) {
        if (input === minInput) hi = lo;
        else lo = hi;
      }

      minInput.value = lo;
      maxInput.value = hi;
      const [minRange, maxRange] = price.querySelectorAll('.js-g24fb-range');
      minRange.value = lo;
      maxRange.value = hi;
      this.paintRange(price);
    }

    paintRange(price) {
      const max = Number(price.dataset.max) || 1;
      const [minRange, maxRange] = price.querySelectorAll('.js-g24fb-range');
      if (!minRange || !maxRange) return;
      price.style.setProperty('--g24fb-lo', `${(Number(minRange.value) / max) * 100}%`);
      price.style.setProperty('--g24fb-hi', `${(Number(maxRange.value) / max) * 100}%`);
      // Der obere Regler liegt oben; stehen beide rechts, muss der untere greifbar bleiben.
      minRange.style.zIndex = Number(minRange.value) >= max - 1 ? '3' : '';
    }

    /* ---------- Parameter und Neuladen ---------- */

    formParams() {
      const params = new URLSearchParams();
      new FormData(this.form).forEach((value, key) => {
        if (value !== '') params.append(key, value);
      });

      // Preis nur mitschicken, wenn er von 0 bzw. vom Maximum abweicht.
      this.querySelectorAll('.g24fb__price').forEach((price) => {
        const max = Number(price.dataset.max) || 0;
        const minInput = price.querySelector('.js-g24fb-price[data-role="min"]');
        const maxInput = price.querySelector('.js-g24fb-price[data-role="max"]');
        if (minInput && Number(minInput.value) <= 0) params.delete(minInput.name);
        if (maxInput && Number(maxInput.value) >= max) params.delete(maxInput.name);
      });

      return params;
    }

    captureState() {
      const openFilter = this.querySelector('.g24fb__filter.is-open');
      const state = {
        openKey: openFilter ? openFilter.dataset.key : null,
        drawerOpen: this.classList.contains('is-drawer-open'),
        search: {},
        panelScroll: 0,
        drawerScroll: 0,
        focus: null
      };

      this.querySelectorAll('.js-g24fb-search').forEach((input) => {
        if (input.value) state.search[input.closest('.g24fb__filter').dataset.key] = input.value;
      });

      if (openFilter) state.panelScroll = openFilter.querySelector('.g24fb__panel').scrollTop;
      const list = this.querySelector('.g24fb__filters');
      if (list) state.drawerScroll = list.scrollTop;

      const active = document.activeElement;
      if (active && this.contains(active)) {
        if (active.name && active.type === 'checkbox') state.focus = `input[name="${CSS.escape(active.name)}"][value="${CSS.escape(active.value)}"]`;
        else if (active.id) state.focus = `#${CSS.escape(active.id)}`;
        else if (active.matches('.js-g24fb-search')) state.focus = 'search';
      }

      return state;
    }

    restoreState(state) {
      Object.keys(state.search).forEach((key) => {
        const filter = this.querySelector(`.g24fb__filter[data-key="${CSS.escape(key)}"]`);
        const input = filter && filter.querySelector('.js-g24fb-search');
        if (input) {
          input.value = state.search[key];
          this.filterOptions(input);
        }
      });

      if (state.openKey) {
        const filter = this.querySelector(`.g24fb__filter[data-key="${CSS.escape(state.openKey)}"]`);
        if (filter) {
          this.openFilter(filter);
          filter.querySelector('.g24fb__panel').scrollTop = state.panelScroll;
          if (state.focus === 'search') {
            const input = filter.querySelector('.js-g24fb-search');
            if (input) input.focus({ preventScroll: true });
          }
        }
      }

      if (state.drawerOpen) {
        this.classList.add('is-drawer-open');
        const opener = this.querySelector('.js-g24fb-open');
        if (opener) opener.setAttribute('aria-expanded', 'true');
      }

      const list = this.querySelector('.g24fb__filters');
      if (list) list.scrollTop = state.drawerScroll;

      if (state.focus && state.focus !== 'search') {
        const el = this.querySelector(state.focus);
        if (el) el.focus({ preventScroll: true });
      }

      this.syncAllPrices();
    }

    async apply(params, { push = true } = {}) {
      params.delete('page');
      params.delete('section_id');
      // Standardsortierung nicht in die URL schreiben.
      if (params.get('sort_by') === this.dataset.defaultSort) params.delete('sort_by');
      const query = params.toString();
      const requestId = ++this.requestId;
      const { results } = this;

      this.classList.add('is-loading');
      if (results) results.classList.add('g24fb-loading');

      let html;
      try {
        const response = await fetch(`${window.location.pathname}?${query}${query ? '&' : ''}section_id=${encodeURIComponent(this.sectionId)}`);
        if (!response.ok) throw new Error(response.status);
        html = await response.text();
      } catch (err) {
        // Notbremse: normal navigieren, dann filtert Shopify serverseitig.
        if (requestId === this.requestId) window.location.search = query;
        return;
      }

      if (requestId !== this.requestId) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newBar = doc.querySelector('g24-filter-bar');
      const newResults = doc.getElementById('filter-results');

      if (newResults && results) results.innerHTML = newResults.innerHTML;

      if (newBar) {
        const state = this.captureState();
        this.innerHTML = newBar.innerHTML;
        this.restoreState(state);
      }

      if (push) {
        window.history.pushState({ g24fb: query }, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
      }

      this.classList.remove('is-loading');
      if (results) results.classList.remove('g24fb-loading');

      if (typeof initLazyImages === 'function') initLazyImages();
      const pagination = document.querySelector('custom-pagination');
      if (pagination && typeof pagination.reload === 'function') pagination.reload();
      document.dispatchEvent(new CustomEvent('on:facet-filters:updated', { bubbles: true }));
      // Merkliste (Wishlist Prime) bindet neu geladene Kachel-Herzen nicht von selbst.
      if (window.iWish && typeof window.iWish.checkIwishColl === 'function') window.iWish.checkIwishColl();
    }
  }

  customElements.define('g24-filter-bar', G24FilterBar);
}
